/**
 * ExportMenu — comparison workspace top-bar export control (P2-4).
 *
 * Consolidates the five export formats (md / pdf / docx / redline / package)
 * behind a single dropdown trigger so the @veridoc/ui workspace surface owns
 * its own export entry. Previously the audit-review formats (md / pdf / docx)
 * lived only on the demo's ReviewHistory page, which made them unreachable
 * from the core workspace and from any host that integrates @veridoc/ui
 * without re-implementing the demo page.
 *
 * The link `href`s point at the streaming export endpoint (no in-memory
 * buffering); the browser handles the download. Each option carries the same
 * `data-testid` (`export-<format>`) the demo page emits so existing e2e
 * selectors keep working whether the host uses the menu or the demo page.
 */
import { useEffect, useRef, useState } from 'react';

import { veridocApi } from '../../lib/veridoc-api';

export type ExportFormat = 'md' | 'pdf' | 'docx' | 'redline' | 'package' | 'client-summary';

interface ExportOption {
  format: ExportFormat;
  label: string;
  title: string;
  tone: 'neutral' | 'brand' | 'primary';
}

const EXPORT_OPTIONS: readonly ExportOption[] = [
  {
    format: 'md',
    label: '导出 Markdown',
    title: '导出纯文本 Markdown 审阅报告',
    tone: 'neutral',
  },
  {
    format: 'pdf',
    label: '导出 PDF',
    title: '导出 PDF 审阅报告（含中文字体）',
    tone: 'neutral',
  },
  {
    format: 'docx',
    label: '导出 Word',
    title: '导出 Word 文档（.docx）审阅报告',
    tone: 'neutral',
  },
  {
    format: 'client-summary',
    label: '导出客户汇报',
    title: '导出客户汇报（按已接受 / 已拒绝 / 待决策分组，不含内部标注）',
    tone: 'neutral',
  },
  {
    format: 'redline',
    label: '导出红线',
    title: '导出可在 Word 中接受/拒绝修订的红线文档（Track Changes）',
    tone: 'brand',
  },
  {
    format: 'package',
    label: '导出定稿包',
    title: '导出定稿交付包（干净稿 + 红线 + 决策清单 + 完整性标记）',
    tone: 'primary',
  },
] as const;

const TONE_LINK_CLASS: Record<ExportOption['tone'], string> = {
  neutral: 'text-ink-700 hover:bg-ink-50 hover:text-ink-900',
  brand: 'text-brand-700 hover:bg-brand-50',
  primary: 'text-brand-700 hover:bg-brand-50',
};

export interface ExportMenuProps {
  comparisonId: string;
  /** Visually disable the trigger (e.g. comparison still processing). */
  disabled?: boolean;
}

export function ExportMenu({
  comparisonId,
  disabled = false,
}: ExportMenuProps): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click — same pattern as FilterPopover.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape so keyboard users aren't trapped.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (!comparisonId) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="true"
        title="导出比对结果"
        data-testid="export-menu-trigger"
        className="inline-flex h-btn-sm items-center gap-1 rounded-sm border border-ink-200 bg-ink-50 px-2 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <span aria-hidden="true">↓</span>
        <span className="hidden md:inline">导出</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="导出格式选择"
          className="absolute right-0 top-full z-50 mt-1 w-56 rounded-md border border-ink-400 bg-ink-0 py-2 shadow-lg"
        >
          {EXPORT_OPTIONS.map((opt) => (
            <a
              key={opt.format}
              data-testid={`export-${opt.format}`}
              href={veridocApi.exportUrl(comparisonId, opt.format)}
              download
              title={opt.title}
              onClick={() => setOpen(false)}
              className={`flex items-center justify-between gap-2 px-3 py-1 text-xs ${TONE_LINK_CLASS[opt.tone]}`}
            >
              <span>{opt.label}</span>
              {opt.tone === 'primary' && (
                <span className="rounded-sm bg-brand-100 px-1 text-[10px] font-medium text-brand-700">
                  推荐
                </span>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
